'use client'

import React from 'react'

import {
  faSpinnerThird,
  faStop,
  faCircleCheck,
} from '@document-crunch/pro-regular-svg-icons'

import { Button } from '@dc-shared-web/components/shadcn/button'
import { Icon } from '@dc-shared-web/components/base/Icon'
import { cn } from '@dc-shared-web/lib'
import {
  LOADING_CONFIG,
  LOADING_MESSAGE_CYLCES,
  LoadingMessageCycle,
  MainLoadingMessage,
} from '@dc-shared-web/components/features/Chat/constants'
import { ChatSubStatus } from '@dc-shared-web/providers/ChatProvider/types'

export interface ChatLoadingMessageProps {
  isReceiving: boolean
  isSuccess?: boolean
  onStop: () => void
  hasStreamingMessage?: boolean
  subStatus?: ChatSubStatus
}

export interface LoadingMessageState {
  mainMessage: string
  subMessage: string
  isInitialPhase: boolean
  index: number
}

export interface LoadingMessageConfig {
  initialMessage: string
  initialDuration: number
  rotationInterval: number
  transitionDuration: number
}

export type MainMessage = MainLoadingMessage

// Robot message styling to match existing chat messages
export const ROBOT_MESSAGE_CLASSNAME = cn(
  'tw-bg-neutral-50 tw-w-[calc(100%_-_68px)] tw-mr-[26px] tw-rounded-md tw-px-3 tw-ml-3.5 tw-mr-4 tw-py-1.5 tw-text-xs tw-relative',
  'after:tw-w-[26px] after:tw-left-[-26px] after:tw-h-[26px] after:tw-bg-white after:tw-rounded-br-[10px] after:tw-absolute after:tw-bottom-0 after:tw-z-10',
  'before:tw-w-[18px] before:tw-left-[-7px] before:tw-h-[26px] before:tw-bg-neutral-50 before:tw-rounded-br-[16px_14px] before:tw-absolute before:tw-bottom-0 before:tw-z-10'
)

// Animation classes
const LOADING_MESSAGE_ANIMATION = cn(
  'tw-transition-all tw-duration-300 tw-ease-in',
  'tw-transform tw-will-change-[opacity,transform]'
)
const FADE_IN_CLASS = 'tw-opacity-100 tw-translate-y-0'
const FADE_OUT_CLASS = 'tw-opacity-50 tw-translate-y-[-2px]'

const getNextMessage = (
  previousIndex: number = -1
): LoadingMessageCycle & { index: number } => {
  const nextIndex = previousIndex + 1
  const cycle =
    LOADING_MESSAGE_CYLCES[nextIndex % LOADING_MESSAGE_CYLCES.length]

  return {
    ...cycle,
    index: nextIndex,
  }
}

export const ChatLoadingMessage: React.FC<ChatLoadingMessageProps> = ({
  isReceiving,
  isSuccess = false,
  onStop,
  hasStreamingMessage = false,
  subStatus,
}) => {
  const [messageState, setMessageState] = React.useState<LoadingMessageState>({
    mainMessage: LOADING_CONFIG.initialMessage,
    subMessage: '',
    isInitialPhase: true,
    index: -1,
  })
  const [isTransitioning, setIsTransitioning] = React.useState(false)
  const [isSuccessAnimatingOut, setIsSuccessAnimatingOut] = React.useState(true)
  const [displayMessageStateInSingleLine, setDisplayMessageStateInSingleLine] =
    React.useState(false)

  // Refs to manage timers and prevent overlapping animations
  const rotationTimerRef = React.useRef<NodeJS.Timeout>()
  const transitionTimerRef = React.useRef<NodeJS.Timeout>()
  const successAnimationTimerRef = React.useRef<NodeJS.Timeout>()

  // Effect to handle success animation
  React.useEffect(() => {
    if (isSuccess) {
      // When success starts, mark animation as not complete
      setIsSuccessAnimatingOut(false)

      // Start animation after a brief delay to show the success message
      successAnimationTimerRef.current = setTimeout(() => {
        setIsSuccessAnimatingOut(true)
      }, 700) // Show success message for 1 second before animating out
    }
  }, [isSuccess])

  React.useEffect(() => {
    setDisplayMessageStateInSingleLine(
      subStatus === ChatSubStatus.ProcessingCitations
    )
  }, [subStatus])

  React.useEffect(() => {
    if (!isReceiving && !isSuccess) {
      // Clear all timers
      if (rotationTimerRef.current) clearInterval(rotationTimerRef.current)
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current)

      // Reset state when not receiving and not in success
      setMessageState({
        mainMessage: LOADING_CONFIG.initialMessage,
        subMessage: '',
        isInitialPhase: true,
        index: -1,
      })
      setIsTransitioning(false)
      return
    }

    if (subStatus === ChatSubStatus.ProcessingCitations) {
      if (rotationTimerRef.current) clearInterval(rotationTimerRef.current)
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current)
      setMessageState({
        mainMessage: 'Processing citations...',
        subMessage: '',
        isInitialPhase: false,
        index: -1,
      })
      setIsTransitioning(false)
      return
    }

    rotationTimerRef.current = setTimeout(
      () => {
        setIsTransitioning(true)

        transitionTimerRef.current = setTimeout(() => {
          const nextMessageCycle = getNextMessage(messageState.index)

          setMessageState({
            mainMessage: nextMessageCycle.mainMessage,
            subMessage: nextMessageCycle.subMessage,
            isInitialPhase: false,
            index: nextMessageCycle.index,
          })
          setIsTransitioning(false)
        }, LOADING_CONFIG.transitionDuration)
      },
      // Rotate to the next message every rotationInterval, unless its the first message
      // ie show "Working..." for 2 seconds and all other messages for 5s
      messageState.index === -1
        ? LOADING_CONFIG.initialDuration
        : LOADING_CONFIG.rotationInterval
    )

    // Cleanup timers
    return () => {
      if (rotationTimerRef.current) clearInterval(rotationTimerRef.current)
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current)
    }
  }, [
    isReceiving,
    hasStreamingMessage,
    isSuccess,
    subStatus,
    messageState.index,
  ])

  // Show success state
  if (isSuccess) {
    return (
      <div
        key={`success-chat-${isSuccessAnimatingOut}`}
        data-testid='chat-success-message'
        className={cn(
          ROBOT_MESSAGE_CLASSNAME,
          'tw-transition-all tw-duration-300 tw-ease-in-out tw-transform',
          isSuccessAnimatingOut || !isReceiving
            ? 'tw-opacity-0 tw-translate-y-full'
            : 'tw-opacity-100 tw-translate-y-0'
        )}
      >
        <div className='tw-flex tw-justify-between tw-items-start tw-relative tw-z-20 tw-min-h-[40px]'>
          <div className='tw-flex tw-gap-2 tw-items-start tw-min-w-0 tw-flex-1'>
            <div className='tw-flex-shrink-0 tw-mt-0.5'>
              <Icon
                icon={faCircleCheck}
                size='lg'
                className='tw-text-green-600'
              />
            </div>
            <div className='tw-flex tw-flex-col tw-gap-1 tw-min-w-0'>
              <span className='tw-text-neutral-700 tw-font-medium tw-py-0.5'>
                Ok, I&apos;m Done
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Hide loading state only if not receiving
  if (!isReceiving) {
    return null
  }

  if (hasStreamingMessage && subStatus !== ChatSubStatus.ProcessingCitations) {
    return (
      <div className='tw-flex tw-justify-between tw-items-center tw-px-3'>
        <div className='tw-flex tw-gap-2 tw-items-center'></div>
        <div className='tw-flex tw-items-center'>
          <Button variant='icon' onClick={onStop} className='tw-p-0'>
            <Icon icon={faStop} />
            <span className='tw-text-sm tw-ml-1 tw-text-neutral-300 tw-font-light'>
              CANCEL
            </span>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid='chat-loading-message'
      className={cn(ROBOT_MESSAGE_CLASSNAME)}
    >
      <div
        className={cn(
          'tw-flex tw-justify-between tw-relative tw-z-20',
          displayMessageStateInSingleLine ? 'tw-items-center' : 'tw-items-start'
        )}
      >
        <div
          className={cn(
            'tw-flex tw-gap-2 tw-min-w-0 tw-flex-1',
            displayMessageStateInSingleLine
              ? 'tw-items-center'
              : 'tw-items-start'
          )}
        >
          <div
            className={cn(
              'tw-flex-shrink-0',
              !displayMessageStateInSingleLine && 'tw-mt-0.5'
            )}
          >
            <Icon icon={faSpinnerThird} size='sm' spin />
          </div>
          <div
            className={cn('tw-flex tw-flex-col tw-gap-1 tw-min-w-0')}
            style={{
              // Inline styles for better browser animation compatibility
              // Also needed to keep
              WebkitBackfaceVisibility: 'hidden',
              backfaceVisibility: 'hidden',
            }}
          >
            <span className='tw-text-neutral-700 tw-font-medium'>
              {messageState.mainMessage}
            </span>
            {!messageState.isInitialPhase &&
              messageState.subMessage &&
              !displayMessageStateInSingleLine && (
                <span
                  className={cn(
                    'tw-text-neutral-500 tw-text-xs',
                    LOADING_MESSAGE_ANIMATION,
                    isTransitioning ? FADE_OUT_CLASS : FADE_IN_CLASS
                  )}
                >
                  {messageState.subMessage}
                </span>
              )}
          </div>
        </div>
        {subStatus !== ChatSubStatus.ProcessingCitations && (
          <div className='tw-flex tw-items-center tw-flex-shrink-0'>
            <Button
              variant='icon'
              onClick={onStop}
              className={cn(
                'tw-p-0',
                'tw-transition-opacity tw-duration-200', // Subtle hover animation
                'hover:tw-opacity-80'
              )}
            >
              <Icon icon={faStop} size='sm' />
              <span className='tw-text-xs tw-ml-1 tw-text-neutral-500 tw-font-light'>
                CANCEL
              </span>
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
